import { useEffect, useState } from 'react';
import { Table, Button, Alert, Spinner, Modal, Form, Row, Col } from 'react-bootstrap';
import { api } from '../api/client';
import { formatDate } from '../shared/dateFormat';
import ResetPasswordModal from './ResetPasswordModal';

export default function AdminManageStudents() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [adjustFor, setAdjustFor] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows(await api.get('/admin/students')); }
    catch (e) { setMsg({ type: 'danger', text: e.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-center py-5"><Spinner /></div>;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Manage Students</h3>
        <Button onClick={() => setShowAdd(true)}>+ Add Student</Button>
      </div>
      {msg.text && <Alert variant={msg.type} dismissible onClose={() => setMsg({ type: '', text: '' })}>{msg.text}</Alert>}
      <Table bordered hover responsive className="bg-white">
        <thead className="table-dark"><tr><th>Name</th><th>Email</th><th>Phone</th><th>Grade</th><th>City</th><th className="text-end">Wallet</th><th>Joined</th><th>Actions</th></tr></thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id}>
              <td>{s.name}</td><td>{s.email}</td><td>{s.phone || '-'}</td>
              <td>{s.grade || '-'}</td><td>{s.city || '-'}</td>
              <td className="text-end">₹{Number(s.wallet_balance).toFixed(2)}</td>
              <td>{formatDate(s.created_at)}</td>
              <td>
                <Button size="sm" variant="outline-primary" className="me-1" onClick={() => setAdjustFor(s)}>Adjust wallet</Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setResetFor(s)}>Reset password</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {adjustFor && <WalletAdjustModal student={adjustFor} onClose={() => setAdjustFor(null)} onDone={() => { setAdjustFor(null); load(); }} />}
      {resetFor && <ResetPasswordModal user={resetFor} onClose={() => setResetFor(null)} onDone={(text) => { setResetFor(null); setMsg({ type: 'success', text }); }} />}
      {showAdd && <AddStudentModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); setMsg({ type: 'success', text: 'Student added.' }); }} />}
    </>
  );
}

function WalletAdjustModal({ student, onClose, onDone }) {
  const [type, setType] = useState('credit');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await api.post(`/admin/students/${student.id}/wallet`, { type, amount: Number(amount), reason });
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>Adjust wallet — {student.name}</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        <p>Current balance: <strong>₹{Number(student.wallet_balance).toFixed(2)}</strong></p>
        <Form.Group className="mb-3"><Form.Label>Type</Form.Label><Form.Select value={type} onChange={(e) => setType(e.target.value)}><option value="credit">Credit (add)</option><option value="debit">Debit (subtract)</option></Form.Select></Form.Group>
        <Form.Group className="mb-3"><Form.Label>Amount (₹)</Form.Label><Form.Control type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} /></Form.Group>
        <Form.Group><Form.Label>Reason</Form.Label><Form.Control value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g., goodwill credit" /></Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={busy || !amount}>{busy ? '...' : 'Apply'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function AddStudentModal({ onClose, onDone }) {
  const [f, setF] = useState({
    name: '', email: '', phone: '', password: '',
    grade: '', board: '', city: '', locality: '', wallet_balance: 0,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await api.post('/admin/students', { ...f, wallet_balance: Number(f.wallet_balance) || 0 });
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton><Modal.Title>Add Student</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        <Row>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Full name *</Form.Label><Form.Control value={f.name} onChange={set('name')} required /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Email *</Form.Label><Form.Control type="email" value={f.email} onChange={set('email')} required /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Phone</Form.Label><Form.Control value={f.phone} onChange={set('phone')} /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Password *</Form.Label><Form.Control type="text" value={f.password} onChange={set('password')} placeholder="min 6 chars" required /></Form.Group></Col>
          <Col md={3}><Form.Group className="mb-3"><Form.Label>Grade</Form.Label><Form.Control value={f.grade} onChange={set('grade')} placeholder="e.g., Class 10" /></Form.Group></Col>
          <Col md={3}><Form.Group className="mb-3"><Form.Label>Board</Form.Label><Form.Control value={f.board} onChange={set('board')} placeholder="CBSE / ICSE..." /></Form.Group></Col>
          <Col md={3}><Form.Group className="mb-3"><Form.Label>City</Form.Label><Form.Control value={f.city} onChange={set('city')} /></Form.Group></Col>
          <Col md={3}><Form.Group className="mb-3"><Form.Label>Locality</Form.Label><Form.Control value={f.locality} onChange={set('locality')} /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Initial wallet credit (₹)</Form.Label><Form.Control type="number" min={0} value={f.wallet_balance} onChange={set('wallet_balance')} /><Form.Text>Optional — seeds student wallet so they can book demos immediately.</Form.Text></Form.Group></Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={busy || !f.name || !f.email || !f.password}>{busy ? 'Creating...' : 'Create'}</Button>
      </Modal.Footer>
    </Modal>
  );
}
